import React, { useMemo } from 'react';

import { FormattedDate, FormattedMessage, type MessageDescriptor, useIntl } from 'react-intl-next';

import Lozenge from '@atlaskit/lozenge';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Inline, xcss } from '@atlaskit/primitives';
import {
	type ExternalUser,
	getProductAndIDFromARI,
	type SearchResultProjectPartial,
	type SearchResultWorkItemPartial,
} from '@atlassian/search-client';

import { ProductKeys } from '../../../../../../../common/constants/products';
import { DescriptionBody } from '../../../../../../../common/ui/description-body';
import { LastModified } from '../../../../../../../common/ui/last-modified';
import { ProfileAvatar } from '../../../../../../../common/ui/profile-avatar';
import { RestrictedIcon } from '../../../../../../../common/ui/restricted-icon';
import SearchMetadataLink from '../../../../../../../common/ui/search-metadata-link';
import { SearchResult } from '../../../../../../../common/ui/search-result';
import type { AdvancedSearchResultInteractedMetadata } from '../../../../../../../common/utils/events/advanced-search-result-interacted';
import {
	enrichExcerpt,
	isNonPublic,
	processExcerpt,
} from '../../../../../../../common/utils/search-result-utils';
import { useFireContextMenuButtonClicked } from '../../../../../../../controllers/events/use-fire-context-menu-button-clicked';
import { useFireContextMenuCopyButtonClicked } from '../../../../../../../controllers/events/use-fire-context-menu-copy-button-clicked';
import { useFireCopyLinkButtonClicked } from '../../../../../../../controllers/events/use-fire-copy-link-button-clicked';
import { type useFireResultClicked } from '../../../../../../../controllers/events/use-fire-result-clicked';
import { type useFireResultCtxMenuClicked } from '../../../../../../../controllers/events/use-fire-result-context-menu-clicked';
import { useFireSummarizeInChatButtonClicked } from '../../../../../../../controllers/events/use-fire-summarize-in-chat-button-clicked';
import { useApplicationMode, useBootstrap } from '../../../../../../../controllers/store/bootstrap';
import { usePrimaryProduct } from '../../../../../../../controllers/store/bootstrap/accessors';
import { useSelectedProduct } from '../../../../../../../controllers/store/search';

import { messages } from './messages';

const nameContainer = xcss({
	whiteSpace: 'nowrap',
});

const statusAndDateContainerStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	gap: 'space.100',
});

interface ProjectSearchResultProps {
	partial: SearchResultProjectPartial;
	position?: number;
	icon: React.ReactNode;
	product?: string;
	onResultClicked?: ReturnType<typeof useFireResultClicked>;
	onResultCtxMenuClicked?: ReturnType<typeof useFireResultCtxMenuClicked>;
}

interface WorkItemSearchResultProps {
	partial: SearchResultWorkItemPartial;
	position?: number;
	icon: React.ReactNode;
	product?: string;
	onResultClicked?: ReturnType<typeof useFireResultClicked>;
	onResultCtxMenuClicked?: ReturnType<typeof useFireResultCtxMenuClicked>;
}

const getStatusLozengeAppearance = (label: string) => {
	label = label.replace(/[\s]+/g, '').toLowerCase();
	switch (true) {
		case ['onhold'].includes(label):
			return 'default'; // gray

		case ['solved', 'complete'].includes(label):
			return 'success'; // green

		case ['open', 'offtrack'].includes(label):
			return 'removed'; // red

		case ['new', 'atrisk'].includes(label):
			return 'moved'; // yellow

		case ['pending', 'ontrack'].includes(label):
			return 'inprogress'; // blue
	}
	return 'inprogress'; // blue for custom status labels
};

const getStatusAndDate = (status?: string, dueDate?: string) => {
	if (!status) {
		return null;
	}
	const statusLabel = status.replace(/[-_]+/g, ' ');

	const statusLozenge = (
		<Lozenge appearance={getStatusLozengeAppearance(statusLabel)}>{statusLabel}</Lozenge>
	);
	const dateLozenge = (
		<Lozenge>
			<FormattedDate value={dueDate} month="short" day="numeric" />
		</Lozenge>
	);

	return (
		<Box xcss={statusAndDateContainerStyles}>
			{dueDate ? (
				<FormattedMessage {...messages.for} values={{ statusLozenge, dateLozenge }} />
			) : (
				statusLozenge
			)}
		</Box>
	);
};

const getAssigneeOrCreator = (
	formatMessage: (descriptor: MessageDescriptor, parameters?: any) => string,
	assignee?: ExternalUser,
	createdBy?: ExternalUser,
) => {
	let actionLabel: string;
	const fullName = assignee?.thirdPartyUser?.name || createdBy?.thirdPartyUser?.name || '';
	const firstName = fullName?.split(' ')?.[0];
	if (assignee?.thirdPartyUser?.name) {
		actionLabel = formatMessage(messages.isAssigned, {
			firstName,
			isAssigned: firstName ? 'internal' : 'external',
		});
	} else {
		actionLabel = formatMessage(messages.created, {
			firstName,
			created: firstName ? 'internal' : 'external',
		});
	}

	return (
		<Inline space="space.025" alignBlock="center">
			<ProfileAvatar name={fullName} />
			<Box xcss={nameContainer}>{actionLabel}</Box>
		</Inline>
	);
};

const formatKey = (key: string | undefined) => (key ? key + ' ' : '');

export const JiraDCWorkItemSearchResult = ({
	partial,
	position,
	icon,
	product,
	onResultClicked,
	onResultCtxMenuClicked,
}: WorkItemSearchResultProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const [selectedProduct] = useSelectedProduct();
	const [primaryProduct] = usePrimaryProduct();
	const [{ cloudId, thirdPartyConfigs }] = useBootstrap();
	const appMode = useApplicationMode();

	const metadata = useMemo(() => {
		const [product, , , contentId] = getProductAndIDFromARI(partial.id);
		return {
			id: partial.id,
			containerId: null,
			spaceId: null,
			contentType: partial.type,
			entityType: partial.type,
			contentId,
			product,
		} satisfies AdvancedSearchResultInteractedMetadata;
	}, [partial.id, partial.type]);

	const onCopyLinkButtonClicked = useFireCopyLinkButtonClicked('searchResultTitle');
	const onSummarizeInChatButtonClicked = useFireSummarizeInChatButtonClicked({
		location: 'searchResult',
		metadata,
		position,
	});
	const onContextMenuCopyButtonClicked = useFireContextMenuCopyButtonClicked();
	const onContextMenuButtonClicked = useFireContextMenuButtonClicked();

	const { lastModifiedDate, url, permissionLevel, entity, excerpt, title } = partial;
	const { assignee, createdBy, dueDate, status, subtype, team } = entity || {};

	const lastModifiedComponent = lastModifiedDate ? (
		<LastModified lastModified={lastModifiedDate} />
	) : null;

	const padlockIcon = isNonPublic(permissionLevel) ? (
		<RestrictedIcon permissionLevel={permissionLevel} />
	) : null;

	const subtypeMap: Record<string, any> = {
		work_item: messages.workItemLabel,
		issue: messages.issueLabel,
		other: messages.workItemLabel,
	};

	const label = formatMessage(
		subtypeMap[subtype?.toLowerCase() ?? 'other'] || messages.workItemLabel,
	);

	// Find the specific datasource for this result based on its integrationId
	const resultDatasource =
		thirdPartyConfigs?.[ProductKeys.JiraDC]?.datasources?.find(
			(ds) => ds.integrationId === partial.integrationId,
		) || null;
	const site =
		resultDatasource?.workspaceName ||
		thirdPartyConfigs?.[ProductKeys.JiraDC]?.workspaceName ||
		null;

	const containerName = entity?.container?.displayName;
	const containerUrl = entity?.container?.url;
	const container =
		containerUrl && containerName ? (
			<SearchMetadataLink label={containerName} href={containerUrl} />
		) : (
			containerName
		);

	const headerMetadataParts = [
		label,
		['site', site],
		['container', container],
		['last-modified', lastModifiedComponent],
		['padlock', padlockIcon],
		['status', getStatusAndDate(status, dueDate)],
		['person', getAssigneeOrCreator(formatMessage, assignee, createdBy)],
	];

	return (
		<SearchResult
			id={partial.id}
			product={product}
			productSelected={!!selectedProduct}
			onResultClicked={onResultClicked}
			onResultContextMenu={onResultCtxMenuClicked}
			onCopyLinkButtonClicked={onCopyLinkButtonClicked}
			onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
			onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
			onContextMenuButtonClicked={onContextMenuButtonClicked}
			icon={icon}
			description={<DescriptionBody description={processExcerpt(excerpt, true)} />}
			headerMetadataParts={headerMetadataParts}
			title={enrichExcerpt(title, formatKey(team))}
			url={url}
			isThirdParty
			cloudId={cloudId}
			primaryProduct={primaryProduct}
			position={position}
			partial={partial}
			appMode={appMode}
		/>
	);
};

export const JiraDCProjectSearchResult = ({
	partial,
	position,
	icon,
	product,
	onResultClicked,
	onResultCtxMenuClicked,
}: ProjectSearchResultProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const [selectedProduct] = useSelectedProduct();
	const [primaryProduct] = usePrimaryProduct();
	const [{ cloudId, thirdPartyConfigs }] = useBootstrap();
	const appMode = useApplicationMode();

	const metadata = useMemo(() => {
		const [product, , , contentId] = getProductAndIDFromARI(partial.id);
		return {
			id: partial.id,
			containerId: null,
			spaceId: null,
			contentType: partial.type,
			entityType: partial.type,
			contentId,
			product,
		} satisfies AdvancedSearchResultInteractedMetadata;
	}, [partial.id, partial.type]);

	const onCopyLinkButtonClicked = useFireCopyLinkButtonClicked('searchResultTitle');
	const onSummarizeInChatButtonClicked = useFireSummarizeInChatButtonClicked({
		location: 'searchResult',
		metadata,
		position,
	});
	const onContextMenuCopyButtonClicked = useFireContextMenuCopyButtonClicked();
	const onContextMenuButtonClicked = useFireContextMenuButtonClicked();

	const { lastModifiedDate, url, permissionLevel, entity, excerpt, title } = partial;
	const { assignee, createdBy, dueDate, status, key } = entity || {};

	const lastModifiedComponent = lastModifiedDate ? (
		<LastModified lastModified={lastModifiedDate} />
	) : null;

	const padlockIcon = isNonPublic(permissionLevel) ? (
		<RestrictedIcon permissionLevel={permissionLevel} />
	) : null;

	const label = formatMessage(messages.issueLabel);

	// Find the specific datasource for this result based on its integrationId
	const resultDatasource =
		thirdPartyConfigs?.[ProductKeys.JiraDC]?.datasources?.find(
			(ds) => ds.integrationId === partial.integrationId,
		) || null;
	const site =
		resultDatasource?.workspaceName ||
		thirdPartyConfigs?.[ProductKeys.JiraDC]?.workspaceName ||
		null;

	const containerName = entity?.container?.displayName;
	const containerUrl = entity?.container?.url;
	const container =
		containerUrl && containerName ? (
			<SearchMetadataLink label={containerName} href={containerUrl} />
		) : (
			containerName
		);

	const headerMetadataParts = [
		label,
		['site', site],
		['container', container],
		['last-modified', lastModifiedComponent],
		['padlock', padlockIcon],
		['status', getStatusAndDate(status, dueDate)],
		['person', getAssigneeOrCreator(formatMessage, assignee, createdBy)],
	];

	return (
		<SearchResult
			id={partial.id}
			product={product}
			productSelected={!!selectedProduct}
			onResultClicked={onResultClicked}
			onResultContextMenu={onResultCtxMenuClicked}
			onCopyLinkButtonClicked={onCopyLinkButtonClicked}
			onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
			onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
			onContextMenuButtonClicked={onContextMenuButtonClicked}
			icon={icon}
			description={<DescriptionBody description={processExcerpt(excerpt, true)} />}
			headerMetadataParts={headerMetadataParts}
			title={enrichExcerpt(title, formatKey(key))}
			url={url}
			isThirdParty
			cloudId={cloudId}
			primaryProduct={primaryProduct}
			position={position}
			partial={partial}
			appMode={appMode}
		/>
	);
};
